import moment from 'moment';

// Intl replaces moment-timezone, whose bundled zone database dominated the first page load.

export const guessTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/** Minutes `timeZone` is ahead of UTC at `instant`. */
export function timeZoneOffset(timeZone: string, instant: Date): number {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    }).formatToParts(instant);
  } catch {
    // an unknown zone in a stored user setting falls back to local time, as moment-timezone did
    return -instant.getTimezoneOffset();
  }
  const part = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)!.value);
  const wallClockAsUtc = Date.UTC(part('year'), part('month') - 1, part('day'), part('hour'), part('minute'), part('second'));
  return Math.round((wallClockAsUtc - Math.floor(instant.getTime() / 1000) * 1000) / 60000);
}

/** `value` as a moment showing the wall-clock time in `timeZone`. */
export function inTimeZone(value: moment.MomentInput, timeZone: string): moment.Moment {
  const result = moment(value);
  return result.utcOffset(timeZoneOffset(timeZone, result.toDate()));
}

/** The instant at which the wall clock in `timeZone` reads the given time. `month` is zero based. */
export function fromTimeZone(timeZone: string, year: number, month: number, day: number, hour = 0, minute = 0, second = 0, millisecond = 0): Date {
  const wallClockAsUtc = Date.UTC(year, month, day, hour, minute, second, millisecond);
  const firstGuess = wallClockAsUtc - timeZoneOffset(timeZone, new Date(wallClockAsUtc)) * 60000;
  // a second pass corrects the guess when a DST change falls between the two instants
  return new Date(wallClockAsUtc - timeZoneOffset(timeZone, new Date(firstGuess)) * 60000);
}
